import Indicator from "./Indicator";
import Image from "next/legacy/image";
import styled from "styled-components";
import * as vars from "../../shared/variables";
import {useContext, useEffect, useState} from "react";
import MiniCartProduct from "../product/MiniCartProduct";
import Button from "../Button";
import Icon from "../Icon";
import {useWishlists} from "../../hooks/useWishlists";
import {useUserLoggedIn} from "../../hooks/useUserLoggedIn";
import {getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem} from "../../shared/localStorageUtil";
import Select from 'react-select'
import {SelectedWishlistContext} from "../../contexts/SelectedWishlistContext";
import {useWishlistById} from "../../hooks/useWishlistById";
import {Spinner} from "react-bootstrap";
import {useMessages} from "../../hooks/useMessages";
import {LoggedOutWishlistContext} from "../../contexts/LoggedOutWishlistContext";
import {ILoggedOutShoppingList} from "../../interfaces/ILoggedOutShoppingList";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {AddItemType} from "../../types/AddItemType";
import {useCountryLang} from "../../hooks/useCountryLang";
import {LoggedOutCartContext} from "../../contexts/LoggedOutCartContext";
import * as _ from "lodash"
import {getNavPath} from "../../shared/navigateUtil";
import Link from "next/link";
import {useRouter} from "next/router";
import {request} from "../../shared/httpInterceptor";
import {ShowLoginContext} from "../../contexts/ShowLoginContext";
import {getTheme} from "../../shared/themeUtils";

const WishlistOffCanvas = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px;
`

const Main = styled.div`
  height: 100%;
  overflow-y: auto;

  .spinner-contain {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: ${getTheme("space.4")};
  }
  
  .login-wishlist {
    border: 1px solid ${getTheme("colors.textOrange")};
    border-radius: ${getTheme("borderRadius")};
    padding: ${getTheme("space.1")};
    color: ${getTheme("colors.secondaryFont")};
    font-size: ${getTheme("fontSizes.small")};
    text-align: center;
    margin-bottom: ${getTheme("space.2")};
    width: 100%;
    background: ${getTheme("colors.white")};
  }
`

const Bottom = styled.div`
  color: ${getTheme("colors.primary")};

  button {
    margin-top: 10px;
  }
`

const Header = styled.div`
  color: ${getTheme("colors.primary")};
  font-weight: 500;
  padding-bottom: ${getTheme("space.1")};

  .top {
    display: flex;
    align-items: center;
    padding: 0 10px 20px 0;
  }

  .selection {
    font-weight: normal;

    .wishlist-select {
      margin: 0 ${getTheme("space.1")};
    }
  }

  span {
    margin-left: 10px;
  }
`

const EmptyWishlist = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${getTheme("colors.secondaryFont")};
  margin-bottom: 60px;
  text-align: center;
`

interface SelectOption {
  value: number,
  label: string
}

const IndicatorWishlist = () => {
  const [hasItems, setHasItems] = useState(false);
  const isUserLoggedIn = useUserLoggedIn();
  const wishlistsQuery = useWishlists();
  const [wishlistsNumber, setWishlistsNumber] = useState(0);
  const [selectOptions,setSelectOptions] = useState<SelectOption[]>([]);
  const [selectedWishlistOption, setSelectedWishlistOption] = useState<SelectOption>();
  const {selectedWishlist, setSelectedWishlist} = useContext(SelectedWishlistContext);
  const wishlistByIdQuery = useWishlistById(selectedWishlistOption?.value)
  const [indicatorTitle, setIndicatorTitle] = useState("")
  const messagesQuery = useMessages()
  const {loggedOutWishlistCtx} = useContext(LoggedOutWishlistContext);
  const countryLang = useCountryLang();
  const queryClient = useQueryClient();
  const {setLoggedOutCartCtx} = useContext(LoggedOutCartContext)
  const router = useRouter()
  const {setShowLoginContext} = useContext(ShowLoginContext);

  useEffect(() => {
    setHasItems(false)
    if (messagesQuery.data) {
      setIndicatorTitle(messagesQuery.data.messages["wishlist"])
    }
    if (wishlistsQuery.data && wishlistsQuery.data.length > 0) {
      setWishlistsNumber(wishlistsQuery.data.length)

      // we populate the wishlist selector with options
      const wishlistsOptions = []
      for (let i = 0; i < wishlistsQuery.data.length; i++) {
        const title = wishlistsQuery.data[i].title
        wishlistsOptions.push({
          value: wishlistsQuery.data[i].id,
          label: title != null ? title : wishlistsQuery.data[i].timestamp
        })
      }
      setSelectOptions(wishlistsOptions)

      // If there's a selected option in local storage we use it as default
      // We store the selection in local storage to persist between page reloads
      const localStorageSelectedWishlist = getLocalStorageItem("selectedWishlistOption")
      // We check if the localStorage value is still a valid option
      let localStorageValueIsValid = false;
      if (localStorageSelectedWishlist != null) {
        localStorageValueIsValid = wishlistsOptions.some((list: SelectOption) => JSON.parse(localStorageSelectedWishlist).value == list.value)
      }
      if (localStorageValueIsValid && localStorageSelectedWishlist != null) {
        setSelectedWishlistOption(JSON.parse(localStorageSelectedWishlist))
      } else {
        // else we set the default selection to the newest created wishlist
        const newestWishlist = wishlistsOptions[wishlistsOptions.length - 1];
        setLocalStorageItem("selectedWishlistOption", JSON.stringify(newestWishlist))
        setSelectedWishlistOption(newestWishlist)
      }

      // we pass the fetchedById wishlist to context
      if (wishlistByIdQuery.data && wishlistsQuery.data) {
        setSelectedWishlist(wishlistByIdQuery.data)
        // If the selected wishlist contains no products we show the empty placeholder
        if (wishlistByIdQuery.data.products && wishlistByIdQuery.data.products.length > 0) {
          setHasItems(true)
        }
      }
    }
    // if there are no wishlists we remove the value stored in local storage (if any)
    else if (wishlistsQuery.isSuccess && !wishlistsQuery.data) {
      removeLocalStorageItem("selectedWishlistOption")
    }

    // logged-out user
    if (loggedOutWishlistCtx != null && loggedOutWishlistCtx.length > 0) {
      setHasItems(true)
    }

  }, [getLocalStorageItem("loggedOutWishlist"), getLocalStorageItem("selectedWishlistOption"), wishlistsQuery.data, wishlistByIdQuery.data, messagesQuery.data])


  const handleWishlistChange = (e: any) => {
    const selected = selectOptions.find((option: any) => option.value == e.value)
    // On change, we update the localStorage for persistence and update the state as well
    if (selected) {
      setLocalStorageItem("selectedWishlistOption", JSON.stringify(selected))
      setSelectedWishlistOption(selected)
      if (wishlistsQuery.data) {
        setSelectedWishlist(wishlistByIdQuery.data)
      }
    }
  }


  const addAllToCartMutation = useMutation(async (data: AddItemType[]) => {
    await request({url: `/${countryLang}/addMultipleToCart`, data: data, method: 'post'})
    await queryClient.cancelQueries(["shopping-cart", countryLang]);
    await queryClient.invalidateQueries(["shopping-cart", countryLang]);
  })

  const areAllProductsInCart = () => {
    const localCartItems = getLocalStorageItem("loggedOutCart");
    const localWishlistItems = getLocalStorageItem("loggedOutWishlist")

    if (localCartItems != null && localWishlistItems) {
      const cartItemsIds = JSON.parse(localCartItems).map((entry: ILoggedOutShoppingList) => entry.product.productID)
      const wishlistItemsIds = JSON.parse(localWishlistItems).map((entry: ILoggedOutShoppingList) => entry.product.productID)
      return _.difference(wishlistItemsIds, cartItemsIds).length === 0;
    }
  }


  const handleAddAllToCart = () => {
    // logged-out user
    if (loggedOutWishlistCtx != null) {
      const loggedOutCart = getLocalStorageItem("loggedOutCart");
      if (loggedOutCart != null) {
        const cartItems = JSON.parse(loggedOutCart)
        const newCart: ILoggedOutShoppingList[] = _.uniqBy(([...cartItems, ...loggedOutWishlistCtx]), "product.productID")
        setLocalStorageItem("loggedOutCart", JSON.stringify(newCart))
        setLoggedOutCartCtx(newCart)
      } else {
        setLocalStorageItem("loggedOutCart", JSON.stringify(loggedOutWishlistCtx))
        setLoggedOutCartCtx(loggedOutWishlistCtx)
      }
    }
    // logged-in user
    else {
      if (selectedWishlist && selectedWishlist.products) {
        const items: AddItemType[] = []
        selectedWishlist.products.map(entry => {
          items.push({
            productId: entry.productId,
            quantity: entry.quantity
          })
        })
        addAllToCartMutation.mutate(items)
      }
    }
  }


  const handleToWishlists = () => {
    router.push(`/${countryLang}/account/wishlists`)
  }


  const handleShowLogin = () => {
    setShowLoginContext(true)
  }


  const emptyWishlist = (
    <EmptyWishlist>
      <Image src="/icons/heart-flat.svg" width={120} height={200} alt="Empty Wishlist"/>
      {messagesQuery.data && <span>{messagesQuery.data.messages["empty-wishlist"]}</span>}
    </EmptyWishlist>
  )

  const wishlistElements = (
    <>
      <Main>
        {/* While the wishlist is being fetched we show the spinner */}
        {wishlistByIdQuery.data && wishlistByIdQuery.isLoading &&
          <div className="spinner-contain">
            <Spinner animation="border"/>
          </div>
        }

        {!isUserLoggedIn && messagesQuery.data &&
          <button className="login-wishlist"
                  onClick={handleShowLogin}>
            {messagesQuery.data.messages["signin-to-save-wishlist"]}
          </button>
        }

        {/* If fetch is successful and we have products we list them */}
        {((isUserLoggedIn && wishlistByIdQuery.data) && (selectedWishlist != null && selectedWishlist.products)) &&
          selectedWishlist?.products.map((entry: any) => (
            <MiniCartProduct
              key={entry.product.productID}
              product={entry.product}
              quantity={entry.quantity}
              type="wishlist"
            />
          ))
        }

        {/* If the user is logged out we render the loggedOutWishlist
        * that's persisted in localStorage */}
        {(!isUserLoggedIn && loggedOutWishlistCtx != null) && loggedOutWishlistCtx.map((entry: ILoggedOutShoppingList) => (
          <MiniCartProduct
            key={entry.product.productID}
            product={entry.product}
            quantity={entry.quantity}
            type="wishlist"
          />
        ))}
      </Main>
      <Bottom>
        {(messagesQuery.data && isUserLoggedIn) &&
          <Button width="100%" variant={"primary"} onClick={handleToWishlists}>
            {messagesQuery.data.messages["my-wishlists"]}
          </Button>}

        {/* If not all products are in cart we show the add to cart option */}
        {(messagesQuery.data && !areAllProductsInCart()) &&
          <Button width="100%" variant="secondary" onClick={handleAddAllToCart}>
            {messagesQuery.data.messages["add-all-to-cart"]}
          </Button>}

        {/* If all the items are already in cart we show the go to cart button*/}
        {(messagesQuery.data && areAllProductsInCart()) &&
          <Link href={getNavPath(router, 'cart')}>
            <Button width="100%" variant={"primary"}>
              {messagesQuery.data.messages["go-to-cart"]}
            </Button>
          </Link>
        }
      </Bottom>
    </>
  )

  const content = (
    <WishlistOffCanvas>
      <Header>
        <div className="top">
          <Icon variant={"medium"} color={vars.primaryColor}>heart-line</Icon>
          <span>{indicatorTitle}</span>
        </div>

        {wishlistsNumber > 1 &&
          <div className="selection">
            <Select options={selectOptions}
                    onChange={handleWishlistChange}
                    value={selectedWishlistOption}
                    isSearchable={false}
                    className={"wishlist-select"}
            />
          </div>}
      </Header>

      {hasItems && wishlistElements}
      {!hasItems && emptyWishlist}
    </WishlistOffCanvas>
  )

  return (
    <Indicator placement={"end"} content={content} iconVariant={"medium"} icon="heart-line"/>
  )
}

export default IndicatorWishlist;
